import { normalizeExperience } from '../../utils/normalizeExperience';
import { getAccessDevices } from '../../utils/experienceDeviceAccess';
import { getExperienceLocationHaystack } from '../../utils/experienceLocation';
import { SYNONYM_EXPANSIONS } from './intentMappings';

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'i', 'me', 'my', 'we', 'our', 'you', 'your',
  'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does',
  'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'shall', 'can',
  'to', 'of', 'in', 'for', 'on', 'with', 'at', 'by', 'from', 'as', 'into', 'through',
  'that', 'this', 'these', 'those', 'it', 'its', 'they', 'them', 'their', 'there',
  'what', 'which', 'who', 'whom', 'when', 'where', 'why', 'how', 'all', 'each',
  'every', 'both', 'few', 'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not',
  'only', 'own', 'same', 'so', 'than', 'too', 'very', 'just', 'about', 'above',
  'after', 'again', 'against', 'am', 'any', 'because', 'before', 'between', 'both',
  'during', 'each', 'else', 'ever', 'get', 'got', 'here', 'if', 'else', 'also',
  'something', 'anything', 'looking', 'look', 'want', 'wants', 'wanted', 'like',
  'love', 'prefer', 'rather', 'really', 'maybe', 'please', 'need', 'know', 'think',
  'say', 'said', 'tell', 'told', 'make', 'made', 'go', 'going', 'went', 'come',
  'came', 'see', 'seen', 'find', 'found', 'use', 'using', 'used', 'try', 'trying',
  'thing', 'things', 'one', 'ones', 'lot', 'lots', 'bit', 'kind', 'type', 'way',
  'much', 'many', 'well', 'still', 'even', 'though', 'while', 'over', 'under',
  'out', 'up', 'down', 'off', 'then', 'now', 'today', 'tonight', 'tomorrow',
  'week', 'hour', 'hours', 'minute', 'minutes', 'spend', 'spending', 'willing',
  'able', 'around', 'within', 'without', 'across', 'along', 'among', 'upon'
]);

function joinParts(parts) {
  return parts
    .flat()
    .filter(Boolean)
    .map((p) => String(p).toLowerCase().trim())
    .filter(Boolean)
    .join(' ');
}

function extractPriceValue(priceLabel) {
  if (!priceLabel) return null;
  const matches = String(priceLabel).match(/\$[\d,.]+/g);
  if (!matches?.length) return null;
  const values = matches.map((m) => parseFloat(m.replace(/[$,]/g, ''))).filter((n) => !Number.isNaN(n));
  return values.length ? Math.min(...values) : null;
}

/**
 * Build a normalized searchable document for one experience.
 * @param {object} rawExperience
 * @returns {import('./experienceSearch').SearchDocument}
 */
export function buildSearchDocument(rawExperience) {
  const exp = normalizeExperience(rawExperience);
  if (!exp) return null;

  const accessDevices = getAccessDevices(exp).map((d) => d.device);
  const deviceTerms = accessDevices.flatMap((d) => SYNONYM_EXPANSIONS[d] || [d]);

  const themes = (exp.themes || []).join(' ');
  const activities = (exp.activities || []).join(' ');
  const bestFor = (exp.bestFor || []).join(' ');
  const groupSize = (exp.groupSize || []).join(' ');
  const tags = (exp.tags || []).join(' ');
  const alsoAvailableOn = (exp.alsoAvailableOn || []).join(' ');
  const comePrepared = (exp.comePrepared || []).join(' ');
  const accessibility = (exp.accessibilityFeatures || []).join(' ');
  const contentNotes = (exp.contentNotes || []).join(' ');
  const locomotion = (exp.locomotionTypes || []).join(' ');

  const locationHaystack = getExperienceLocationHaystack(exp);
  const minPrice = extractPriceValue(exp.priceLabel);

  const fields = {
    title: exp.title || '',
    description: exp.description || '',
    officialDescription: exp.officialDescription || '',
    preview: exp.realityCheckPreview || '',
    category: exp.category || '',
    categoryChip: exp.categoryChip || '',
    contentType: exp.contentType || '',
    primaryFormat: exp.primaryFormat || '',
    formatBadge: exp.formatBadge || '',
    formatDescriptor: exp.formatDescriptor || '',
    creator: exp.creatorName || '',
    publishPlatform: exp.publishPlatform || '',
    priceLabel: exp.priceLabel || '',
    priceType: exp.priceType || '',
    duration: exp.duration || '',
    session: exp.typicalSessionLabel || '',
    ageRating: exp.ageRating || '',
    comfort: exp.comfortRating || '',
    effort: exp.effortSignal || '',
    venue: [exp.venueName, exp.venueCity, exp.venueAddress, exp.venueCountry].filter(Boolean).join(' '),
    location: locationHaystack,
    themes,
    activities,
    bestFor,
    groupSize,
    tags,
    devices: [...accessDevices, ...deviceTerms].join(' '),
    platforms: [...(exp.platformLabels || []), ...(exp.deviceLabels || []), alsoAvailableOn].join(' '),
    delivery: (exp.deliveryTechnology || []).join(' '),
    accessSurface: (exp.accessSurface || []).join(' '),
    guidance: exp.guidanceLevel || '',
    replayability: exp.replayability || '',
    multiplayer: exp.multiplayer ? 'multiplayer social' : '',
    comePrepared,
    accessibility,
    contentNotes,
    locomotion,
    hostPlatform: exp.hostPlatform || '',
    sessionStructure: exp.sessionStructure || '',
    availability: exp.availabilityStatusLabel || ''
  };

  const haystack = joinParts(Object.values(fields));

  return {
    id: exp.id,
    experience: rawExperience,
    titleLower: (exp.title || '').toLowerCase(),
    haystack,
    fields,
    accessDevices,
    priceType: (exp.priceType || '').toLowerCase(),
    minPrice,
    isFree: /\bfree\b/i.test(exp.priceLabel || '') || exp.priceType === 'free',
    isHeadsetOnly: accessDevices.length === 1 && accessDevices[0] === 'headset',
    hasMobile: accessDevices.includes('mobile'),
    hasHeadset: accessDevices.includes('headset'),
    hasDesktop: accessDevices.includes('desktop'),
    category: exp.category || '',
    contentType: exp.contentType || '',
    themes: exp.themes || [],
    bestFor: exp.bestFor || [],
    groupSize: exp.groupSize || [],
    locationHaystack: locationHaystack,
    comfortRating: exp.comfortRating || ''
  };
}

/**
 * Pre-build search documents for a list of experiences.
 * @param {object[]} experiences
 * @returns {Map<string, import('./experienceSearch').SearchDocument>}
 */
export function buildSearchIndex(experiences) {
  const index = new Map();
  for (const exp of experiences || []) {
    const doc = buildSearchDocument(exp);
    if (doc) index.set(doc.id, doc);
  }
  return index;
}

export function tokenizeQuery(query) {
  return String(query)
    .toLowerCase()
    .replace(/[^\w\s$°.-]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 1 && !STOP_WORDS.has(t));
}
